<template>
    <Transition name="onsite-banner">
        <div
            v-if="show && targetEvent && !isInsideConferenceApp"
            class="fixed top-0 inset-x-0 z-[60] flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#995449] to-[#7a3f36] text-white shadow-lg print:hidden"
            :style="{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.625rem)' }"
        >
            <i class="fal fa-location-dot text-base flex-shrink-0" />
            <div class="flex-1 min-w-0 leading-tight">
                <div class="text-[11px] font-bold uppercase tracking-widest text-white/70">You're onsite</div>
                <div class="text-sm font-semibold truncate">{{ headline }}</div>
            </div>
            <router-link
                :to="`/events/${targetEvent.id}/app`"
                class="flex-shrink-0 bg-white text-[#995449] text-xs font-bold rounded-full px-3 py-1.5 active:scale-95 transition-transform"
            >
                Open App
                <i class="fal fa-chevron-right ml-1 text-[10px]" />
            </router-link>
            <button
                @click="dismiss"
                class="flex-shrink-0 w-7 h-7 flex items-center justify-center text-white/70 active:text-white"
                aria-label="Dismiss"
            >
                <i class="fal fa-times text-sm" />
            </button>
        </div>
    </Transition>
</template>

<script>
import dayjs from 'dayjs'
import Mango from '../../helpers/mango'

const BLACK_MOUNTAIN = { lat: 35.6184, lng: -82.3214 }
const RADIUS_MILES = 50
const DISMISS_KEY_PREFIX = 'onsite-banner-dismissed-'
const DISMISS_TTL_MS = 1000 * 60 * 60 * 6
const GEO_CACHE_KEY = 'onsite-banner-geo'

function haversineMiles(a, b) {
    const toRad = (d) => d * Math.PI / 180
    const R = 3958.8
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
    return 2 * R * Math.asin(Math.sqrt(x))
}

export default {
    inject: ['store'],
    data() {
        return {
            show: false,
            targetEvent: null,
            checked: false,
        }
    },
    computed: {
        isInsideConferenceApp() {
            return /^\/events\/[^/]+\/app/.test(this.$route.path)
        },
        headline() {
            if (!this.targetEvent) return ''
            const start = this.targetEvent.startDate ? dayjs(this.targetEvent.startDate) : null
            const end = this.targetEvent.endDate ? dayjs(this.targetEvent.endDate) : null
            const now = dayjs()
            if (start && end && now.isAfter(start) && now.isBefore(end)) return `${this.targetEvent.title} is happening now`
            if (start && start.isAfter(now)) {
                const diff = start.diff(now, 'day')
                if (diff <= 0) return `${this.targetEvent.title} starts today`
                if (diff === 1) return `${this.targetEvent.title} starts tomorrow`
                return `${this.targetEvent.title} starts in ${diff} days`
            }
            return this.targetEvent.title
        },
    },
    mounted() {
        if (this.checked) return
        this.checked = true
        if (this.isDismissed()) return
        this.run()
    },
    methods: {
        isDismissed() {
            try {
                const raw = window.localStorage.getItem(DISMISS_KEY_PREFIX + this.todayKey())
                if (!raw) return false
                const ts = parseInt(raw, 10)
                return Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS
            } catch { return false }
        },
        todayKey() {
            return dayjs().format('YYYY-MM-DD')
        },
        dismiss() {
            this.show = false
            try { window.localStorage.setItem(DISMISS_KEY_PREFIX + this.todayKey(), String(Date.now())) } catch {}
        },
        async run() {
            // HAP-4635: event first, location second. Almost every page load happens when no
            // Black Mountain event is live or within a week, so this returns before any location
            // lookup at all. When one is, location comes from our own API (Cloudflare's IP-geo
            // headers), never from a browser permission prompt on page load and never from
            // ipapi.co (rate-limited → 429 without CORS → console error on every page).
            const event = await this.findActiveEvent()
            if (!event) return
            const here = await this.resolveLocation()
            if (!here) return
            if (haversineMiles(here, BLACK_MOUNTAIN) > RADIUS_MILES) return
            this.targetEvent = event
            this.show = true
        },
        async resolveLocation() {
            try {
                const cached = window.sessionStorage.getItem(GEO_CACHE_KEY)
                if (cached) return JSON.parse(cached)
            } catch {}
            let here = null
            try {
                const controller = new AbortController()
                const timer = setTimeout(() => controller.abort(), 4000)
                const res = await fetch(`${this.store.api}/controllers/visitorGeo`, { signal: controller.signal })
                clearTimeout(timer)
                const data = res.ok ? await res.json() : null
                if (typeof data?.lat === 'number' && typeof data?.lng === 'number') here = { lat: data.lat, lng: data.lng }
            } catch {}
            try { window.sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(here)) } catch {}
            return here
        },
        async findActiveEvent() {
            const now = new Date()
            const windowStart = dayjs(now).subtract(1, 'day').toDate()
            const windowEnd = dayjs(now).add(7, 'day').toDate()
            try {
                const events = await Mango.events({
                    search: {
                        compareEndDate: { greaterThan: windowStart },
                        compareStartDate: { lessThan: windowEnd },
                    },
                    fields: ['id', 'title', 'startDate', 'endDate', 'address'],
                    sort: { startDate: 1 },
                    limit: 10,
                }) || []
                const onsite = events.filter(e => {
                    const lat = e?.address?.coordinates?.lat
                    const lng = e?.address?.coordinates?.lng
                    if (typeof lat !== 'number' || typeof lng !== 'number') return true
                    return haversineMiles({ lat, lng }, BLACK_MOUNTAIN) <= RADIUS_MILES + 5
                })
                const liveNow = onsite.find(e => {
                    if (!e.startDate || !e.endDate) return false
                    const s = dayjs(e.startDate)
                    const f = dayjs(e.endDate)
                    return dayjs(now).isAfter(s) && dayjs(now).isBefore(f)
                })
                if (liveNow) return liveNow
                const upcoming = onsite
                    .filter(e => e.startDate && dayjs(e.startDate).isAfter(now))
                    .sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)))[0]
                return upcoming || onsite[0] || null
            } catch {
                return null
            }
        },
    },
}
</script>

<style scoped>
.onsite-banner-enter-active,
.onsite-banner-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.onsite-banner-enter-from,
.onsite-banner-leave-to {
    opacity: 0;
    transform: translateY(-100%);
}
</style>
